import React, { useMemo, useEffect } from "react";
import { useCommonStateFields } from "features/queryReports/hooks/useCommonStateFields";
import {
  applyCommonOutputs,
  applyGeographyParams
} from "features/queryReports/utils/queryCommon";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import cbsa from "util/cbsa.js";
import states from "util/states.js";
import { underZipLimit, validateZipCodes } from "util/zip.js";
import { readableRefDate } from "util/medicareRefDate.js";
import classes from "./Reports.module.scss";
import { disableSubmitButton, validateFileName } from "features/queryReports/utils/commonFunctions";
import QueryCreatedModal from "features/queryReports/components/QueryCreatedModal";
import { useSubmitQuery } from "features/queryReports/hooks/useSubmitQuery";
import { placeOfServiceIDs } from "features/queryReports/utils/reportIds";
import {
  CBSAInput,
  CheckboxInput,
  FileNameInput,
  GeographyTypeSelect,
  PatientZip3Input,
  ProviderTypeSelect,
  RefDateSelect,
  StateSelect,
  ZipCodeInput
} from "features/queryReports/components/FormFields";
import placeOfServiceOutputFields from "features/queryReports/utils/reportOutputFields/PlaceOfServiceOutputFields";

function QueryLogicDescription({ providerType, comboRefDate, queryObj }) {
  return (
    <span>
      I would like to see{" "}
      <strong>{providerType ? providerType + "s" : ""}</strong> located in the{" "}
      {queryObj.parameters.geography_type === "zipcode" && (
        <strong>
          ZIP Code{queryObj.parameters.zips.length > 1 ? "s" : ""} of{" "}
          {queryObj.parameters.zips.length > 1
            ? queryObj.parameters.zips.join(", ")
            : queryObj.parameters.zips[0] || ""}
        </strong>
      )}
      {queryObj.parameters.geography_type === "cbsa" && (
        <strong>
          CBSA of{" "}
          {queryObj.parameters.cbsa
            ? cbsa.find(({ code }) => code === queryObj.parameters.cbsa).name
            : ""}
        </strong>
      )}
      {queryObj.parameters.geography_type === "state" && (
        <strong>
          State of{" "}
          {queryObj.parameters.state
            ? states.find(({ value }) => value === queryObj.parameters.state)
              .name
            : ""}
        </strong>
      )}
      {queryObj.parameters.geography_type === "patientZip3" && (
        <strong>
          ZIP3 code{queryObj.parameters.patientZip3s.length > 1 ? "s" : ""} of{" "}
          {queryObj.parameters.patientZip3s.length > 1
            ? queryObj.parameters.patientZip3s.join(", ")
            : queryObj.parameters.patientZip3s[0] || ""}
        </strong>
      )}
      {" "}who saw <strong>Medicare and Commercial</strong> patients{" "}
      <span>
        between <strong>{readableRefDate(comboRefDate)}</strong>
      </span>
      .
    </span>
  );
}

function PlaceOfService({ bridge, payerMixValue, initialParams, showOpenMyQueries }) {
  const {
    categoryId,
    physReportId,
    orgReportId
  } = placeOfServiceIDs;
  const {
    state: common,
    bind,
    set,
    setOutput,
    resetCommon,
    hydrateFrom,
    selectors
  } = useCommonStateFields({
    overrides: { payerMix: payerMixValue ?? "" }
  });

  const {
    submitQuery,
    waiting,
    showModal,
    modalData,
    closeModal
  } = useSubmitQuery({
    bridge,
    categoryId,
    classes,
    fileName: common.fileName
  });

  const confirmQuerySuccess = type => {
    if (type === 1) {
      closeModal();
      bridge.onMyQueryOpen();
    } else if (type === 2) {
      closeModal();
      bridge.onQuerySubmitted();
    }
  };

  const applyIncomingParams = data => {
    if (data.reportId === physReportId) {
      set("providerType", "physician");
    } else if (data.reportId === orgReportId) {
      set("providerType", "organization");
    }
  };

  useEffect(() => {
    if (initialParams) {
      hydrateFrom(initialParams);
      applyIncomingParams(initialParams); // local compute set at initial data
      // any page-only hydration here as needed
    }
  }, [initialParams]);

  const queryObj = useMemo(() => {
    const retObj = {
      description: common.fileName,
      parameters: {
        quarter: common.comboRefDate,
        geography_type: common.geographyType
      },
      outputFields: []
    };

    // common outputs
    applyCommonOutputs(retObj, common.outputs, {
      allowList: [
        "address",
        "city",
        "zipCode",
        "source",
        "distinctClaimsCount",
        "distinctPatientsCount",
        "proceduresCount",
        "sumCharges",
        "placeOfService"
      ]
    });

    if (common.providerType === "physician") {
      retObj.reportId = physReportId;

      // Provider outputs using common toggles
      if (common.outputs.npi) retObj.outputFields.push("Physician NPI");
      if (common.outputs.name) retObj.outputFields.push("Physician Name");
      if (common.outputs.classification)
        retObj.outputFields.push("Physician Classification");
      if (common.outputs.specialization)
        retObj.outputFields.push("Physician Specialization");
      if (common.outputs.hospitalAffiliationIds)
        retObj.outputFields.push("Hospital Affiliation IDs");
      if (common.outputs.hospitalAffiliationNames)
        retObj.outputFields.push("Hospital Affiliation Names");
      if (common.outputs.groupPracticeIds)
        retObj.outputFields.push("Group Practice IDs");
      if (common.outputs.groupPracticeNames)
        retObj.outputFields.push("Group Practice Names");
      // Page-only outputs (keep from your local state)
    } else if (common.providerType === "organization") {
      retObj.reportId = orgReportId;
      if (common.outputs.npi) retObj.outputFields.push("Organization NPI");
      if (common.outputs.name) retObj.outputFields.push("Organization Name");
      if (common.outputs.classification)
        retObj.outputFields.push("Organization Classification");
      if (common.outputs.specialization)
        retObj.outputFields.push("Organization Specialization");
    }

    applyGeographyParams(retObj, {
      geographyType: common.geographyType,
      zipCodes: common.zipCodes,
      cbsaSelection: common.cbsaSelection,
      stateSelection: common.stateSelection,
      patientZip3s: common.patientZip3s
    });

    return retObj;
  }, [
    common.providerType,
    common.zipCodes,
    common.fileName,
    common.cbsaSelection,
    common.geographyType,
    common.stateSelection,
    common.patientZip3s,
    common.comboRefDate,
    common.outputs
  ]);

  function clearFields() {
    resetCommon(); // clears shared fields
    // clear page-only here
  }

  return (
    <div className="container-fluid p-3">
      <div className={classes["report-content-wrap"]}>
        <FileNameInput
          fileName={common.fileName}
          setFileName={v => set("fileName", v)}
          priorQueryParams={common.priorQueryParams}
        />
        <ProviderTypeSelect
          bind={bind}
          label="Provider Type:"
          overlayText="Group claim counts by either physician or organizations"
        />

        <RefDateSelect
          id="comborefdate"
          label="Commercial + Medicare Reference Date:"
          {...bind.select("comboRefDate")}
          placeholder="Select Commercial + Medicare Reference Date"
          overlayText="Commercial + Medicare reference date"
        />

        <GeographyTypeSelect bind={bind} common={common} />

        {common.geographyType === "zipcode" && (
          <ZipCodeInput bind={bind} common={common} />
        )}

        {common.geographyType === "cbsa" && (
          <CBSAInput set={set} common={common} />
        )}

        {common.geographyType === "state" && (
          <StateSelect set={set} common={common} />
        )}

        {common.geographyType === "patientZip3" && (
          <PatientZip3Input common={common} bind={bind} />
        )}

        {common.providerType && common.geographyType && (
          <div className="row mb-3">
            <div className={classes["querylogic-wrapper"]}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                className={classes["info-icon"]}
              />
              <div>
                <div className={classes["header"]}>Query Logic</div>
                <div className={classes["body"]}>
                  <QueryLogicDescription
                    providerType={common.providerType}
                    comboRefDate={common.comboRefDate}
                    queryObj={queryObj}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {common.providerType && (
          <div className="row mb-3">
            <div className={classes["querylogic-wrapper"]}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                className={classes["info-icon"]}
              />
              <div>
                <div className={classes["header"]}>Output Fields</div>
                <div className={classes["body"]}>
                  {placeOfServiceOutputFields.map((item, index) => (
                    <CheckboxInput
                      key={item.name || index}
                      item={item}
                      common={common}
                      selectors={selectors}
                      setOutput={setOutput}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {common.fileName &&
          common.providerType &&
          common.comboRefDate &&
          ((common.geographyType === "zipcode" &&
            common.zipCodes &&
            validateZipCodes(common.zipCodes, ["zip", "zip+4"]) &&
            underZipLimit(common.zipCodes, 500)) ||
            (common.geographyType === "cbsa" && common.cbsaSelection) ||
            (common.geographyType === "state" && common.stateSelection) ||
            (common.geographyType === "patientZip3" &&
              common.patientZip3s &&
              validateZipCodes(common.patientZip3s, ["zip3"]) &&
              underZipLimit(common.patientZip3s, 20))) && (
            <div className={`row ${classes["submit-btn-wrap"]}`}>
              <button
                type="button"
                className={`btn btn-primary mr-2 ${classes["submit-btn"]}`}
                disabled={!validateFileName(common.fileName) || disableSubmitButton(common)}
                onClick={() => submitQuery(queryObj)}
              >
                Submit Query
                {waiting && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
              </button>
              <button
                type="button"
                className={`btn btn-secondary ${classes["cancel-btn"]}`}
                onClick={() => clearFields()}
              >
                Cancel
              </button>
            </div>
          )}

        <QueryCreatedModal
          show={showModal}
          header={modalData.header}
          body={modalData.body}
          classes={classes}
          onClose={() => confirmQuerySuccess(2)}
          onOpenMyQueries={() => confirmQuerySuccess(1)}
          showOpenMyQueries={showOpenMyQueries}
        />
      </div>
    </div>
  );
}

export default PlaceOfService;
